import { Typography } from '@material-ui/core';
import Button, { ButtonProps } from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import ListItemText from '@material-ui/core/ListItemText';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from 'clsx';
import truncate from 'lodash/truncate';
import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import colors from '../../themes/main-colors';
import {
  displayAccountIdentifier,
  displayCustomerName,
} from '../../util/format';
import { useComponentSize } from '../../util/style-utils';
import {
  AccountDetail,
  AccountSort,
  AccountType,
} from '../../__generated__/pge-types';
import { AccountListState } from '../account-summary/multi-accounts/accountListReducer';
import SortFilterPageHeader from '../account-summary/sort-filter-page-header/SortFilterPageHeader';
import { PageSize } from '../pagination/types';

type Props = {
  loading: boolean;
  selectedAccount: AccountDetail | undefined;
  accounts: Array<AccountDetail>;
  onSelect: (account: AccountDetail) => void;
  accountListParams: AccountListState;
  totalCount: number;
  showSelectedAccountOnly?: boolean;
  isMobile?: boolean;
  disableNonResAccounts?: boolean;
  onAccountSearch: (search: string) => void;
  onAccountSort: (value: AccountSort) => void;
  onAccountPageChange: (newPage: number, newPageSize: PageSize) => void;
};

interface StyledMenuProps extends MenuProps {
  width: number;
}

const useStyledMenuStyles = makeStyles(() => ({
  paper: {
    width: (props: { width: number }) => props.width,
  },
}));

type StyleProps = {
  showMini: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  menuItem: {
    color: colors.noirBlur,
    '& .primary': {
      paddingRight: theme.spacing(1),
      color: colors.noirBlur,
      '& span': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    '& .secondary': {
      color: colors.noirBlur,
      '& p': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  },
  menuProgress: {
    flex: 1,
  },
  listItem: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
  paging: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    display: 'flex',
    flexDirection: ({ showMini }: StyleProps) => (showMini ? 'column' : 'row'),
    marginBottom: ({ showMini }: StyleProps) =>
      showMini ? theme.spacing(0) : theme.spacing(3),
  },
  defaultCursor: {
    cursor: 'default',
  },
  button: {
    // border: 'none',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '& > span': {
      minWidth: 0,
      display: 'flex',
    },
  },
  buttonLoading: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  dropdownIndicator: {
    borderRight: `1px solid ${colors.noirBlur}`,
    paddingRight: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  menuContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  menu: {
    marginTop: theme.spacing(1),
  },
  innerLoading: {
    textAlign: 'center',
  },
  accountContainer: {
    display: 'flex',
    alignItems: 'center',
    [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
      display: 'block',
    },
  },
  accountDropDownContents: {
    display: 'flex',
  },
}));

const StyledMenu = (props: StyledMenuProps) => {
  const classes = useStyledMenuStyles(props);
  return (
    <Menu
      elevation={1}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={classes}
      {...props}
    />
  );
};

const StyledButton = ({
  isDisplay,
  ...rest
}: ButtonProps & { isDisplay: boolean }) => (
  <Button
    fullWidth
    aria-controls="customized-menu"
    aria-haspopup="true"
    variant="outlined"
    endIcon={isDisplay ? undefined : <KeyboardArrowDownIcon />}
    style={{
      background: '#fff',
      display: 'flex',
    }}
    disableRipple={isDisplay}
    {...rest}
  />
);

function firstServiceAddressLine(account: AccountDetail) {
  return account?.premiseInfo?.[0]?.addressLine1;
}

export default function AccountDropdown({
  loading,
  selectedAccount,
  accounts,
  onSelect,
  accountListParams,
  totalCount,
  showSelectedAccountOnly = false,
  isMobile,
  disableNonResAccounts,
  onAccountSearch,
  onAccountSort,
  onAccountPageChange,
}: Props) {
  const { t } = useTranslation();
  const anchorEl = React.useRef<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);
  const isDisplay = showSelectedAccountOnly;
  const { width } = useComponentSize(anchorEl);

  const theme = useTheme();
  const showMini = width <= theme.breakpoints.values.sm;
  const compact = width <= theme.breakpoints.values.md;
  const classes = useStyles({ showMini });

  const handleClick = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleSelect = (account: AccountDetail) => {
    onSelect(account);
    onClose();
  };

  return (
    <>
      <Grid
        container
        spacing={1}
        direction="row"
        className={classes.accountContainer}
        data-testid="account-dropdown"
      >
        {!isDisplay && (
          <Grid item>
            <Typography variant="body1">{t('SELECT_ACCOUNT_LABEL')}</Typography>
          </Grid>
        )}
        <Grid item style={{ flexGrow: 1 }}>
          {!selectedAccount || (loading && !open) ? (
            <StyledButton
              buttonRef={anchorEl}
              isDisplay
              className={clsx(
                classes.defaultCursor,
                classes.button,
                classes.buttonLoading,
              )}
              onClick={undefined}
            >
              <CircularProgress size={35} />
            </StyledButton>
          ) : (
            <StyledButton
              buttonRef={anchorEl}
              className={clsx(
                isDisplay && classes.defaultCursor,
                classes.button,
                classes.accountContainer,
              )}
              onClick={isDisplay ? undefined : handleClick}
              isDisplay={isDisplay}
            >
              <Grid
                container
                spacing={1}
                style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
              >
                <Grid item xs={showMini ? 12 : 4}>
                  <Grid container spacing={1} direction="row">
                    <Grid item xs={12} zeroMinWidth>
                      <Typography align="left" variant="h3">
                        {selectedAccount.accountNumber}
                      </Typography>
                    </Grid>
                    {selectedAccount.description && (
                      <Grid item xs={12} zeroMinWidth>
                        <Typography align="left" variant="h3">
                          (
                          {showMini
                            ? truncate(selectedAccount.description, {
                                length: 13,
                              })
                            : selectedAccount.description}
                          )
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={showMini ? 12 : 4}>
                  <Typography align="left" variant="body2">
                    {firstServiceAddressLine(selectedAccount)}
                  </Typography>
                </Grid>
                {!showMini && (
                  <Grid item xs={4}>
                    <Typography noWrap align="left" variant="body2">
                      {displayCustomerName(selectedAccount)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </StyledButton>
          )}
        </Grid>
      </Grid>

      <StyledMenu
        className={classes.menu}
        width={width}
        anchorEl={anchorEl.current}
        keepMounted
        open={open}
        onClose={onClose}
      >
        <div className={classes.menuContent}>
          <div className={classes.paging}>
            <SortFilterPageHeader
              onAccountSearch={onAccountSearch}
              onAccountSort={onAccountSort}
              onAccountPageChange={onAccountPageChange}
              accountListParams={accountListParams}
              totalCount={totalCount}
              compact={compact}
            />
          </div>
          <div className={clsx(loading && classes.innerLoading)}>
            {loading && (
              <CircularProgress className={classes.menuProgress} size={35} />
            )}
            {!loading &&
              accounts.map(account => (
                <MenuItem
                  className={clsx(classes.menuItem, classes.listItem)}
                  key={account.accountNumber}
                  selected={
                    account.accountNumber === selectedAccount?.accountNumber
                  }
                  disabled={
                    Boolean(disableNonResAccounts)
                      ? account.accountType !== AccountType.Res
                      : false
                  }
                  onClick={() => handleSelect(account)}
                >
                  <ListItemText
                    primary={displayAccountIdentifier(account)}
                    className="primary"
                    primaryTypographyProps={{
                      style: {
                        fontWeight: 'bold',
                        color: colors.noirBlur,
                      },
                    }}
                  />
                  <ListItemText
                    secondary={firstServiceAddressLine(account)}
                    className="secondary"
                    secondaryTypographyProps={{
                      style: {
                        color: colors.noirBlur,
                      },
                    }}
                  />
                  {!showMini && (
                    <ListItemText
                      secondary={displayCustomerName(account)}
                      className="secondary"
                      secondaryTypographyProps={{
                        style: {
                          color: colors.noirBlur,
                        },
                      }}
                    />
                  )}
                </MenuItem>
              ))}
          </div>
        </div>
      </StyledMenu>
    </>
  );
}
